// When a pending play's game should be underway, derived from the release's own
// `gameTime` with no live feed.
//
// Shared because two very different surfaces need the SAME answer: the admin command
// center uses it to pick which cards get a live skeleton before the first live fetch
// returns, and the results rows use it to decide whether a pending play still reads as
// "Live". The bound is the point — "kickoff has passed" alone is true forever, so an
// ungraded play from last season would claim to be live indefinitely (#2004).

/**
 * How long after kickoff a pending game is still assumed underway (covers a long
 * football game plus overtime). Past this a pending play is late to grade, not live.
 */
export const EXPECTED_LIVE_WINDOW_MS = 4 * 60 * 60 * 1000;

/**
 * True when a release's game should be underway right now — kickoff is in the past but
 * within ~4h and the play isn't graded.
 *
 * This is a guess from the schedule, not a fact: where a real live feed is available
 * (the admin `/stats/releases/live` context) its explicit state wins, and this only
 * covers the gap before that feed answers.
 */
export function isExpectedLive(release: API.Release, now = Date.now()): boolean {
  if (release.result !== 'pending') {
    return false;
  }
  const t = release.gameTime ? new Date(release.gameTime).getTime() : Number.NaN;
  if (!Number.isFinite(t)) {
    return false;
  }
  return t <= now && now <= t + EXPECTED_LIVE_WINDOW_MS;
}
